import { FormulaParseError } from './types';

/** `position`/`end` delimit the token's source extent: [position, end). */
export type Token =
  | { type: 'num'; value: number; position: number; end: number }
  | { type: 'str'; value: string; position: number; end: number }
  | { type: 'ident'; value: string; position: number; end: number }
  | { type: 'field'; value: string; position: number; end: number }
  | { type: 'op'; value: string; position: number; end: number }
  | { type: 'lparen'; position: number; end: number }
  | { type: 'rparen'; position: number; end: number }
  | { type: 'comma'; position: number; end: number }
  | { type: 'eof'; position: number; end: number };

const IDENT_START = /[A-Za-z_]/;
const IDENT_PART = /[A-Za-z0-9_]/;
const DIGIT = /[0-9]/;

export function tokenize(source: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  while (i < source.length) {
    const ch = source[i];
    if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r') {
      i++;
      continue;
    }
    if (ch === '(') {
      tokens.push({ type: 'lparen', position: i, end: i + 1 });
      i++;
      continue;
    }
    if (ch === ')') {
      tokens.push({ type: 'rparen', position: i, end: i + 1 });
      i++;
      continue;
    }
    if (ch === ',') {
      tokens.push({ type: 'comma', position: i, end: i + 1 });
      i++;
      continue;
    }
    if (ch === '[') {
      const close = source.indexOf(']', i + 1);
      if (close === -1) {
        // Ran off the end of the source: the ref is unfinished, not wrong.
        throw new FormulaParseError('Unclosed field reference: missing "]"', i, source.length, true);
      }
      const ref = source.slice(i + 1, close).trim();
      if (!ref) {
        throw new FormulaParseError('Empty field reference "[]"', i, close + 1);
      }
      tokens.push({ type: 'field', value: ref, position: i, end: close + 1 });
      i = close + 1;
      continue;
    }
    if (ch === '<') {
      const next = source[i + 1];
      const op = next === '=' ? '<=' : next === '>' ? '<>' : '<';
      tokens.push({ type: 'op', value: op, position: i, end: i + op.length });
      i += op.length;
      continue;
    }
    if (ch === '>') {
      const op = source[i + 1] === '=' ? '>=' : '>';
      tokens.push({ type: 'op', value: op, position: i, end: i + op.length });
      i += op.length;
      continue;
    }
    if (ch === '"') {
      const start = i;
      let value = '';
      i++;
      for (;;) {
        if (i >= source.length) {
          throw new FormulaParseError(
            "Unterminated string: missing closing '\"'",
            start,
            source.length,
            true
          );
        }
        const c = source[i];
        if (c === '"') {
          // Excel escapes a quote inside a string by doubling it: "" -> "
          if (source[i + 1] === '"') {
            value += '"';
            i += 2;
            continue;
          }
          i++;
          break;
        }
        value += c;
        i++;
      }
      tokens.push({ type: 'str', value, position: start, end: i });
      continue;
    }
    if (ch === '+' || ch === '-' || ch === '*' || ch === '/' || ch === '^' || ch === '=' || ch === '&') {
      tokens.push({ type: 'op', value: ch, position: i, end: i + 1 });
      i++;
      continue;
    }
    if (DIGIT.test(ch) || (ch === '.' && DIGIT.test(source[i + 1] ?? ''))) {
      const start = i;
      while (i < source.length && DIGIT.test(source[i])) {
        i++;
      }
      if (source[i] === '.') {
        i++;
        while (i < source.length && DIGIT.test(source[i])) {
          i++;
        }
      }
      if (source[i] === 'e' || source[i] === 'E') {
        let j = i + 1;
        if (source[j] === '+' || source[j] === '-') {
          j++;
        }
        if (DIGIT.test(source[j] ?? '')) {
          i = j;
          while (i < source.length && DIGIT.test(source[i])) {
            i++;
          }
        }
      }
      // A number immediately followed by "." or an identifier char is malformed (e.g. "1.2.3", "1x")
      if (source[i] === '.' || IDENT_START.test(source[i] ?? '')) {
        throw new FormulaParseError(`Invalid number "${source.slice(start, i + 1)}"`, start, i + 1);
      }
      tokens.push({ type: 'num', value: Number(source.slice(start, i)), position: start, end: i });
      continue;
    }
    if (IDENT_START.test(ch)) {
      const start = i;
      while (i < source.length && IDENT_PART.test(source[i])) {
        i++;
      }
      tokens.push({ type: 'ident', value: source.slice(start, i), position: start, end: i });
      continue;
    }
    throw new FormulaParseError(`Unexpected character "${ch}"`, i, i + 1);
  }
  tokens.push({ type: 'eof', position: source.length, end: source.length });
  return tokens;
}
